import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Settings, Save, Shield, Bell, Globe, Clock, 
  ToggleLeft, ToggleRight, AlertTriangle, Palette, Layers
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ColorField = ({ label, value, onChange, testid }) => (
  <div>
    <label className="block text-sm font-medium text-slate-400 mb-2">{label}</label>
    <div className="flex items-center gap-3">
      <input
        type="color"
        value={value || '#000000'}
        onChange={(e) => onChange(e.target.value)}
        className="h-12 w-14 rounded-lg cursor-pointer bg-slate-700 border border-slate-600"
        data-testid={`${testid}-picker`}
      />
      <input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 bg-slate-700 border border-slate-600 rounded-xl px-4 py-3 text-white font-mono text-sm uppercase focus:ring-2 focus:ring-indigo-500"
        placeholder="#RRGGBB"
        data-testid={`${testid}-input`}
      />
    </div>
  </div>
);

export default function AdminSettings() {
  const [settings, setSettings] = useState({
    platform_name: 'Bid2Barter',
    platform_tagline: 'Trade Anything, Anywhere',
    default_bid_duration_days: 7,
    max_bid_extension_days: 14,
    max_images_per_listing: 10,
    min_bid_amount: 0,
    enable_guest_browsing: true,
    require_email_verification: false,
    require_phone_verification: false,
    enable_notifications: true,
    enable_messaging: true,
    enable_reviews: true,
    maintenance_mode: false,
    maintenance_message: 'We are currently performing maintenance. Please check back soon.',
    theme_primary_color: '#4F46E5',
    theme_accent_color: '#10B981',
    theme_hero_gradient_from: '#0F172A',
    theme_hero_gradient_via: '#312E81',
    theme_hero_gradient_to: '#4C1D95',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/settings`);
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Failed to fetch settings', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/settings`, settings);
      toast.success('Settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const Toggle = ({ enabled, onToggle }) => (
    <button onClick={onToggle} className="focus:outline-none">
      {enabled ? (
        <ToggleRight className="w-10 h-10 text-emerald-500" />
      ) : (
        <ToggleLeft className="w-10 h-10 text-slate-500" />
      )}
    </button>
  );

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Platform Settings
          </h1>
          <p className="text-slate-400">Configure platform-wide settings and preferences</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-indigo-600 hover:bg-indigo-700"
          data-testid="save-settings-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="space-y-6">
        {/* General Settings */}
        <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
            <Globe className="w-5 h-5 text-indigo-400" />
            General Settings
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-2">Platform Name</label>
              <input
                type="text"
                value={settings.platform_name}
                onChange={(e) => handleChange('platform_name', e.target.value)}
                className="w-full bg-slate-700 border border-slate-600 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-2">Platform Tagline</label>
              <input
                type="text"
                value={settings.platform_tagline}
                onChange={(e) => handleChange('platform_tagline', e.target.value)}
                className="w-full bg-slate-700 border border-slate-600 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
        </div>

        {/* Listing Settings */}
        <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-400" />
            Listing Settings
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-2">Default Bid Duration (days)</label>
              <input
                type="number"
                min="1"
                max="30"
                value={settings.default_bid_duration_days}
                onChange={(e) => handleChange('default_bid_duration_days', parseInt(e.target.value))}
                className="w-full bg-slate-700 border border-slate-600 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-2">Max Extension (days)</label>
              <input
                type="number"
                min="1"
                max="30"
                value={settings.max_bid_extension_days}
                onChange={(e) => handleChange('max_bid_extension_days', parseInt(e.target.value))}
                className="w-full bg-slate-700 border border-slate-600 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-2">Max Images Per Listing</label>
              <input
                type="number"
                min="1"
                max="20"
                value={settings.max_images_per_listing}
                onChange={(e) => handleChange('max_images_per_listing', parseInt(e.target.value))}
                className="w-full bg-slate-700 border border-slate-600 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
        </div>

        {/* Feature Toggles */}
        <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
            <Bell className="w-5 h-5 text-emerald-400" />
            Feature Toggles
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-xl">
              <div>
                <p className="text-white font-medium">Guest Browsing</p>
                <p className="text-sm text-slate-400">Allow non-registered users to browse listings</p>
              </div>
              <Toggle 
                enabled={settings.enable_guest_browsing} 
                onToggle={() => handleToggle('enable_guest_browsing')} 
              />
            </div>
            <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-xl">
              <div>
                <p className="text-white font-medium">Notifications</p>
                <p className="text-sm text-slate-400">Enable in-app notifications for users</p>
              </div>
              <Toggle 
                enabled={settings.enable_notifications} 
                onToggle={() => handleToggle('enable_notifications')} 
              />
            </div>
            <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-xl">
              <div>
                <p className="text-white font-medium">Messaging</p>
                <p className="text-sm text-slate-400">Allow users to message each other</p>
              </div>
              <Toggle 
                enabled={settings.enable_messaging} 
                onToggle={() => handleToggle('enable_messaging')} 
              />
            </div>
            <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-xl">
              <div>
                <p className="text-white font-medium">Reviews</p>
                <p className="text-sm text-slate-400">Allow users to leave reviews after trades</p>
              </div>
              <Toggle 
                enabled={settings.enable_reviews} 
                onToggle={() => handleToggle('enable_reviews')} 
              />
            </div>
          </div>
        </div>

        {/* Security Settings */}
        <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
            <Shield className="w-5 h-5 text-violet-400" />
            Security Settings
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-xl">
              <div>
                <p className="text-white font-medium">Email Verification Required</p>
                <p className="text-sm text-slate-400">Require email verification before trading</p>
              </div>
              <Toggle 
                enabled={settings.require_email_verification} 
                onToggle={() => handleToggle('require_email_verification')} 
              />
            </div>
            <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-xl">
              <div>
                <p className="text-white font-medium">Phone Verification Required</p>
                <p className="text-sm text-slate-400">Require phone verification before trading</p>
              </div>
              <Toggle 
                enabled={settings.require_phone_verification} 
                onToggle={() => handleToggle('require_phone_verification')} 
              />
            </div>
          </div>
        </div>

        {/* Theme Customization */}
        <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700" data-testid="theme-settings-section">
          <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
            <Palette className="w-5 h-5 text-pink-400" />
            Theme & Branding
          </h3>
          <p className="text-sm text-slate-500 mb-5">Customize the homepage colors. Changes apply globally after save.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
            <ColorField
              label="Primary Color"
              value={settings.theme_primary_color}
              onChange={(v) => handleChange('theme_primary_color', v)}
              testid="theme-primary-color"
            />
            <ColorField
              label="Accent Color"
              value={settings.theme_accent_color}
              onChange={(v) => handleChange('theme_accent_color', v)}
              testid="theme-accent-color"
            />
          </div>

          <p className="text-sm text-slate-400 mb-3 font-medium">Homepage Hero Gradient</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <ColorField
              label="Gradient From"
              value={settings.theme_hero_gradient_from}
              onChange={(v) => handleChange('theme_hero_gradient_from', v)}
              testid="theme-hero-from"
            />
            <ColorField
              label="Gradient Via"
              value={settings.theme_hero_gradient_via}
              onChange={(v) => handleChange('theme_hero_gradient_via', v)}
              testid="theme-hero-via"
            />
            <ColorField
              label="Gradient To"
              value={settings.theme_hero_gradient_to}
              onChange={(v) => handleChange('theme_hero_gradient_to', v)}
              testid="theme-hero-to"
            />
          </div>

          <div
            className="rounded-xl h-24 border border-slate-700 relative overflow-hidden"
            style={{
              background: `linear-gradient(135deg, ${settings.theme_hero_gradient_from} 0%, ${settings.theme_hero_gradient_via} 50%, ${settings.theme_hero_gradient_to} 100%)`,
            }}
          >
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-white font-bold text-sm tracking-wide">Hero Preview</span>
            </div>
          </div>
        </div>

        {/* Categories Shortcut */}
        <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Layers className="w-5 h-5 text-indigo-400" />
            <div>
              <h3 className="text-lg font-semibold text-white">Manage Categories</h3>
              <p className="text-sm text-slate-400">Add, edit, reorder, enable or disable homepage categories.</p>
            </div>
          </div>
          <Link
            to="/admin/categories"
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium"
            data-testid="open-categories-link"
          >
            Open Categories
          </Link>
        </div>

        {/* Maintenance Mode */}
        <div className="bg-slate-800 rounded-2xl p-6 border border-red-500/30">
          <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            Maintenance Mode
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-red-500/10 rounded-xl">
              <div>
                <p className="text-white font-medium">Enable Maintenance Mode</p>
                <p className="text-sm text-slate-400">Temporarily disable the platform for users</p>
              </div>
              <Toggle 
                enabled={settings.maintenance_mode} 
                onToggle={() => handleToggle('maintenance_mode')} 
              />
            </div>
            {settings.maintenance_mode && (
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">Maintenance Message</label>
                <textarea
                  value={settings.maintenance_message}
                  onChange={(e) => handleChange('maintenance_message', e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-red-500"
                  rows={3}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
